import './Menu.scss';

import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonMenu,
  IonMenuToggle,
} from '@ionic/react';
import {
  briefcase,
  gameController,
  helpCircle,
  home,
  key,
  logOutOutline,
  people,
  person,
  terminal,
} from 'ionicons/icons';
import React, { useEffect, useRef } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import {
  JobsLinks,
  ResourceLink,
  contactLink,
  jettingCalculatorLink,
  productInformationLink,
  trainingLink,
  yourAccountLink,
} from 'src/constants';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { FirmwareMessages } from 'src/intl/FirmwareMessages';
import { GenericMessages } from 'src/intl/GenericMessages';
import { HomeMessages } from 'src/intl/HomeMessages';
import { JobMessages } from 'src/intl/JobMessages';
import { ProfileMessages } from 'src/intl/ProfileMessages';
import { useHasPermission } from 'src/utils/PermissionGuard';
import sentinelLogo from '../assets/images/SentinelLogo.png';
import { AppRouteConstants } from '../pages/app-layout/AppRouteConstants';
import { useFeatureFlag } from '../utils/feature-flags/FeatureFlags';
import { useDispatch, useSelector } from 'react-redux';
import { selectSideMenuOpen } from 'src/redux/common/selectors';
import { commonActions } from 'src/redux/common/actions';

interface AppPage {
  url: string;
  iosIcon: string;
  mdIcon: string;
  title: MessageDescriptor;
  automationId: string;
  nestedMenuItems?: ResourceLink[];
}

const getAppPages = (isBundleFeatureOn: boolean): AppPage[] => {
  return [
    {
      title: HomeMessages.home,
      url: '/home',
      iosIcon: home, //<ion-icon name="home"></ion-icon> <ion-icon name="game-controller"></ion-icon>
      mdIcon: home,
      automationId: 'homeMenu',
    },
    // This menu item changes from a link to logs to an accordion header for logs and bundles depending on the bundles feature flag.
    {
      title: isBundleFeatureOn ? JobMessages.jobsTitle : JobMessages.jobLogsTitle,
      url: isBundleFeatureOn ? '/' : AppRouteConstants.LogsList,
      iosIcon: briefcase,
      mdIcon: briefcase,
      automationId: 'jobsTitleMenu',
      nestedMenuItems: isBundleFeatureOn ? JobsLinks : undefined,
    },
    {
      title: ControllerMessages.controllersHeader,
      url: '/controllers',
      iosIcon: gameController,
      mdIcon: gameController,
      automationId: 'controllersMenu',
    },
    {
      title: ProfileMessages.profile,
      url: '/profile',
      iosIcon: person,
      mdIcon: person,
      automationId: 'profileMenu',
    },
    {
      title: FirmwareMessages.firmware,
      url: '/dev-firmware',
      iosIcon: terminal,
      mdIcon: terminal,
      automationId: 'firmwareMenu',
    },
    {
      title: HomeMessages.subscriptionPageHeader,
      url: '/organization-subscriptions',
      mdIcon: key,
      iosIcon: key,
      automationId: 'subscriptions',
    },
    {
      title: ProfileMessages.stoneAgeUsersLabel,
      url: '/stoneage-users',
      mdIcon: people,
      iosIcon: people,
      automationId: 'stoneAgeUsers',
    },
  ];
};

const externalResourceLinks = [yourAccountLink, jettingCalculatorLink, productInformationLink, trainingLink];

const Menu: React.FC = () => {
  const location = useLocation();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const showFirmware = useHasPermission('ViewFirmware');
  const showJobs = useHasPermission('ViewJobLogs');
  const showControllers = useHasPermission('ViewControllers');
  const showStoneAgeUsers = useHasPermission('ViewStoneAgeUsers');
  const showOrgSubscriptions = useHasPermission('ViewOrganizationSubscriptions');
  const bundlesOn = useFeatureFlag('BUNDLES');
  const appPages = getAppPages(bundlesOn);
  const menuRef = useRef<HTMLIonMenuElement>(null);
  const isSideMenuOpen = useSelector(selectSideMenuOpen);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isSideMenuOpen) {
      menuRef?.current?.open();
    } else {
      menuRef?.current?.close();
    }
  }, [isSideMenuOpen]);

  return (
    <IonMenu
      onIonDidClose={() => dispatch(commonActions.toggleSideMenu())}
      ref={menuRef}
      side="start"
      menuId="first"
      contentId="main"
      className="root-menu"
      swipeGesture={false}
    >
      <IonContent>
        <IonList id="sentinel-link">
          <IonListHeader>
            <img
              src={sentinelLogo}
              alt="stoneage logo"
              style={{
                maxHeight: '50px',
                width: 'auto',
                marginRight: '1em',
              }}
            />
            {formatMessage(GenericMessages.sentinelLink)}
          </IonListHeader>
          {appPages
            .filter(a => showJobs || a.url !== AppRouteConstants.LogsList)
            .filter(a => showControllers || a.url !== '/controllers')
            .filter(a => showFirmware || a.url !== '/dev-firmware')
            .filter(a => showStoneAgeUsers || a.url !== '/stoneage-users')
            .filter(a => showOrgSubscriptions || a.url !== '/organization-subscriptions')
            .map((appPage, index) => {
              if (appPage.nestedMenuItems) {
                return (
                  <IonAccordionGroup key={`${appPage.title}-${index}`}>
                    <IonAccordion className="main-menu-accordion">
                      <IonItem slot="header">
                        <IonIcon slot="start" ios={appPage.iosIcon} md={appPage.mdIcon} color="dark" />
                        <IonLabel>{formatMessage(appPage.title)}</IonLabel>
                      </IonItem>
                      <div slot="content">
                        <IonList className="menu-accordion-list">
                          {appPage.nestedMenuItems.map((link, index) => (
                            <IonMenuToggle key={`${link.title}-${index}`} autoHide={false}>
                              <IonItem
                                lines="none"
                                routerLink={link.actionUrl}
                                routerDirection="root"
                                data-testid={`navTile-${link.automationId}`}
                              >
                                <IonIcon slot="start" icon={link.icon} color="dark" />
                                <IonLabel>{formatMessage(link.title)}</IonLabel>
                              </IonItem>
                            </IonMenuToggle>
                          ))}
                        </IonList>
                      </div>
                    </IonAccordion>
                  </IonAccordionGroup>
                );
              }
              return (
                <IonMenuToggle key={`${appPage.title}-${index}`} autoHide={false}>
                  <IonItem
                    className={location.pathname === appPage.url ? 'selected' : ''}
                    routerLink={appPage.url}
                    routerDirection="root"
                    lines="none"
                    detail={false}
                    data-testid={`navTile-${appPage.automationId}`}
                  >
                    <IonIcon slot="start" ios={appPage.iosIcon} md={appPage.mdIcon} color="dark" />
                    <IonLabel>{formatMessage(appPage.title)}</IonLabel>
                  </IonItem>
                </IonMenuToggle>
              );
            })}
        </IonList>
        <IonAccordionGroup>
          <IonAccordion className="menu-accordion">
            <IonItem slot="header">
              <IonLabel>{formatMessage(GenericMessages.externalLinks)}</IonLabel>
            </IonItem>

            <div slot="content">
              <IonList>
                {externalResourceLinks.map((link, index) => (
                  <IonMenuToggle key={`${link.title}-${index}`} autoHide={false}>
                    <IonItem
                      lines="none"
                      href={link.actionUrl}
                      target="_blank"
                      data-testid={`navTile-${link.automationId}`}
                    >
                      <IonIcon slot="start" icon={link.icon} color="dark" />
                      <IonLabel>{formatMessage(link.title)}</IonLabel>
                    </IonItem>
                  </IonMenuToggle>
                ))}
                <IonMenuToggle autoHide={false}>
                  <IonItem
                    lines="none"
                    href={contactLink.actionUrl}
                    target="_blank"
                    data-testid={`menuItem-${contactLink.automationId}`}
                  >
                    <IonIcon slot="start" icon={helpCircle} color="dark" />
                    <IonLabel>{formatMessage(contactLink.title)}</IonLabel>
                  </IonItem>
                </IonMenuToggle>
              </IonList>
            </div>
          </IonAccordion>
        </IonAccordionGroup>
        <IonButton
          expand="block"
          fill="outline"
          onClick={async () => history.push('/logout')}
          data-testid={`navTile-logOut`}
        >
          <IonIcon slot="start" icon={logOutOutline} />
          {formatMessage(GenericMessages.logOut)}
        </IonButton>
      </IonContent>
    </IonMenu>
  );
};

export default Menu;
